import { FC, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material'
import { usePersonBankAccount, usePersonPartialUpdate } from '@services/contacts/contacts.hooks'
import { Edit as EditIcon } from '@mui/icons-material'
import { useSnackbar } from 'notistack'

interface BankAccountConfirmationProps {
  personId: number | null
  isReadOnly?: boolean
  language?: 'pl' | 'en'
}

// Funkcja walidująca IBAN zgodnie ze standardem ISO 13616 (mod-97)
const validateIBAN = (iban: string): boolean => {
  if (!iban) return false

  // Usuń spacje i konwertuj na wielkie litery
  const cleanIban = iban.replace(/\s/g, '').toUpperCase()

  // Sprawdź format (2 litery + 2 cyfry + 1-30 znaków alfanumerycznych)
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(cleanIban)) {
    return false
  }

  // Sprawdź długość (15-34 znaki zgodnie ze standardem IBAN)
  if (cleanIban.length < 15 || cleanIban.length > 34) {
    return false
  }

  // Przenieś pierwsze 4 znaki na koniec
  const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4)

  // Zamień litery na liczby (A=10, B=11, ..., Z=35)
  const numericString = rearranged
    .split('')
    .map((char) => {
      const code = char.charCodeAt(0)
      return code >= 65 && code <= 90 ? (code - 55).toString() : char
    })
    .join('')

  // Oblicz modulo 97 (algorytm weryfikacji IBAN)
  let remainder = numericString.slice(0, 2)
  for (let offset = 2; offset < numericString.length; offset += 7) {
    const fragment = remainder + numericString.substring(offset, offset + 7)
    remainder = (parseInt(fragment, 10) % 97).toString()
  }

  return parseInt(remainder, 10) === 1
}

const translations = {
  pl: {
    title: 'Potwierdzam powyższy numer rachunku:',
    change: 'Zmień',
    loading: 'Ładowanie danych rachunku...',
    error: 'Błąd podczas pobierania danych rachunku bankowego',
    noAccount: 'Brak numeru rachunku bankowego',
    dialogTitle: 'Edytuj numer rachunku',
    dialogCancel: 'Anuluj',
    dialogSave: 'Zapisz',
    accountNumber: 'Numer rachunku',
    invalidIban: 'Nieprawidłowy format IBAN',
    saveSuccess: 'Numer rachunku został zaktualizowany',
    saveError: 'Błąd podczas zapisywania numeru rachunku',
    saving: 'Zapisywanie...'
  },
  en: {
    title: 'I confirm the above account number:',
    change: 'Change',
    loading: 'Loading account data...',
    error: 'Error loading bank account data',
    noAccount: 'No bank account number',
    dialogTitle: 'Edit account number',
    dialogCancel: 'Cancel',
    dialogSave: 'Save',
    accountNumber: 'Account number',
    invalidIban: 'Invalid IBAN format',
    saveSuccess: 'Account number has been updated',
    saveError: 'Error saving account number',
    saving: 'Saving...'
  }
}

const BankAccountConfirmation: FC<BankAccountConfirmationProps> = ({
  personId,
  isReadOnly = false,
  language = 'pl'
}) => {
  const { data: bankAccount, isLoading, error } = usePersonBankAccount(personId)
  const updatePerson = usePersonPartialUpdate()
  const { enqueueSnackbar } = useSnackbar()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editedAccountNumber, setEditedAccountNumber] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  const t = translations[language]

  const handleOpenDialog = () => {
    setEditedAccountNumber(bankAccount?.bankAccountIban || '')
    setValidationError(null)
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setValidationError(null)
  }

  const handleAccountNumberChange = (value: string) => {
    setEditedAccountNumber(value)
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError(null)
    }
  }

  const handleSave = async () => {
    if (!personId) return

    // Validate IBAN before saving
    if (!validateIBAN(editedAccountNumber)) {
      setValidationError(t.invalidIban)
      return
    }

    try {
      await updatePerson.mutateAsync({
        id: personId,
        data: { bankAccountIban: editedAccountNumber }
      })
      enqueueSnackbar(t.saveSuccess, { variant: 'success' })
      setIsDialogOpen(false)
    } catch (error: any) {
      console.error('Error updating bank account:', error)
      const errorMessage = error?.response?.data
        ? `${t.saveError}: ${JSON.stringify(error.response.data)}`
        : t.saveError
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  }

  if (!personId) {
    return null
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 3 }}>
        <CircularProgress size={20} />
        <Typography variant='body2'>{t.loading}</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity='error' sx={{ my: 3 }}>
        {t.error}
      </Alert>
    )
  }

  const accountNumber = bankAccount?.bankAccountIban

  if (!accountNumber) {
    return (
      <Alert severity='warning' sx={{ my: 3 }}>
        {t.noAccount}
      </Alert>
    )
  }

  return (
    <>
      <Box sx={{ my: 3, width: '100%', marginLeft: '2rem' }}>
        {/* Polish version */}
        <Typography variant='body1' sx={{ mb: 0.5 }}>
          {translations.pl.title}{' '}
          <Typography
            component='span'
            sx={{
              fontFamily: 'monospace',
              fontWeight: 500,
              letterSpacing: 0.5
            }}
          >
            {accountNumber}
          </Typography>
          {!isReadOnly && (
            <Button
              size='small'
              variant='text'
              startIcon={<EditIcon />}
              onClick={handleOpenDialog}
              sx={{ ml: 1, textTransform: 'none' }}
            >
              {translations.pl.change}
            </Button>
          )}
        </Typography>

        {/* English version */}
        <Typography variant='body1' sx={{ fontStyle: 'italic' }}>
          {translations.en.title}{' '}
          <Typography
            component='span'
            sx={{
              fontFamily: 'monospace',
              fontWeight: 500,
              letterSpacing: 0.5,
              fontStyle: 'italic'
            }}
          >
            {accountNumber}
          </Typography>
          {!isReadOnly && (
            <Button
              size='small'
              variant='text'
              startIcon={<EditIcon />}
              onClick={handleOpenDialog}
              sx={{ ml: 1, textTransform: 'none' }}
            >
              {translations.en.change}
            </Button>
          )}
        </Typography>
      </Box>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth='sm' fullWidth>
        <DialogTitle>{t.dialogTitle}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t.accountNumber}
            value={editedAccountNumber}
            onChange={(e) => handleAccountNumberChange(e.target.value)}
            error={!!validationError}
            helperText={validationError}
            disabled={updatePerson.isPending}
            sx={{ mt: 2 }}
            placeholder='PL61109010140000071219812874'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={updatePerson.isPending}>
            {t.dialogCancel}
          </Button>
          <Button onClick={handleSave} variant='contained' disabled={updatePerson.isPending}>
            {updatePerson.isPending ? t.saving : t.dialogSave}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default BankAccountConfirmation
